import { eachDayOfInterval, format, isAfter } from 'date-fns';
import * as yup from 'yup';
import { TIME_REGEX } from '../../../../utils/time';
import type { HalfDay } from './DailyAbsenceForm';

export const createDailyAbsenceValidationSchema = () =>
  yup.object({
    startDate: yup.date().required(),
    startDateHalfDay: yup.mixed<HalfDay>().oneOf(['true', 'false']).required(),
    endDate: yup
      .date()
      .required()
      .when('startDate', {
        is: (startDate: Date | undefined) => startDate != null,
        then: (schema) => schema.min(yup.ref('startDate')),
      }),
    endDateHalfDay: yup.mixed<HalfDay>().oneOf(['true', 'false']).required(),
    note: yup
      .string()
      .notRequired()
      .when('$requireNote', {
        is: true,
        then: (schema) => schema.required(),
      }),
  });

export const createHourlyAbsenceValidationSchema = () =>
  yup
    .object({
      startDate: yup.date().required(),
      endDate: yup
        .date()
        .required()
        .when('startDate', {
          is: (startDate: Date | undefined) => startDate != null,
          then: (schema) => schema.min(yup.ref('startDate')),
        }),
      note: yup
        .string()
        .notRequired()
        .when('$requireNote', {
          is: true,
          then: (schema) => schema.required(),
        }),
    })
    .test('valid-daily-times', function (values: HourlySchemaValues) {
      if (
        !values?.startDate ||
        !values.endDate ||
        isAfter(values.startDate, values.endDate)
      ) {
        return true;
      }

      for (const date of eachDayOfInterval({
        start: values.startDate,
        end: values.endDate,
      })) {
        const dateKey = format(date, 'yyyy-MM-dd');
        const startTimeField = `${dateKey}_startTime`;
        const endTimeField = `${dateKey}_endTime`;
        const startTime = values[startTimeField];
        const endTime = values[endTimeField];

        if (
          typeof startTime === 'string' &&
          startTime !== '' &&
          !TIME_REGEX.test(startTime)
        ) {
          return this.createError({
            path: startTimeField,
            message: { key: 'validations.time.format', values: {} },
          });
        }
        if (
          typeof endTime === 'string' &&
          endTime !== '' &&
          !TIME_REGEX.test(endTime)
        ) {
          return this.createError({
            path: endTimeField,
            message: { key: 'validations.time.format', values: {} },
          });
        }
        if (
          typeof startTime === 'string' &&
          typeof endTime === 'string' &&
          startTime !== '' &&
          endTime !== '' &&
          startTime > endTime
        ) {
          return this.createError({
            path: endTimeField,
            message: { key: 'requestAbsence.errors.timeError', values: {} },
          });
        }
      }

      return true;
    });

type HourlySchemaValues = Record<string, unknown> & {
  startDate?: Date;
  endDate?: Date;
};
